import Component from '@glimmer/component';
import { dropTask, task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { action } from '@ember/object';
import { format, isBefore, startOfDay } from 'date-fns';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import InviteStateLabel from 'garaje/utils/invite-state-label';
import { INVITE_STATE, LENDING_STATUS } from 'garaje/utils/enums';

/**
 * @param {gqlEntry}          entry             graphql representation of an entry
 * @param {gqlInvite}         invite            graphql representation of an invite
 * @param {Number}            maxOccupancy      amount allowed in the office
 * @param {Number}            peopleRegistered  total amount of people with invites for the day (not desk reservations)
 */
export default class ScheduleCardComponent extends Component {
  @service flashMessages;
  @service state;
  @service metrics;
  @service registrations;
  @service deskLending;
  @service scheduleGraphql;

  @tracked unscheduleModalIsOpen = false;
  @tracked unscheduleError = false;
  @tracked groupDeskReservationModalIsOpen = false;
  @tracked reclaimModalIsOpen = false;
  INVITE_STATE_ENUM = INVITE_STATE;

  get spotsRemaining() {
    return this.args.maxOccupancy - this.args.peopleRegistered;
  }

  get atCapacity() {
    return this.spotsRemaining === 0;
  }

  get schedulingBlocked() {
    return this.atCapacity && this.state.currentLocation.capacityLimitEnabled;
  }

  get inviteStateLabel() {
    return InviteStateLabel(this.args.inviteState);
  }

  get lendingStatus() {
    const dateKey = format(this.args.date, 'yyyy-MM-dd');
    return this.args.lendingStatusByDate?.[dateKey]?.lendingStatus ?? LENDING_STATUS.NOT_LENT;
  }

  get isDeskLent() {
    return this.args.deskLendingEnabled === true && this.lendingStatus !== LENDING_STATUS.NOT_LENT;
  }

  get canLendDesk() {
    return (
      this.args.deskLendingEnabled === true &&
      this.lendingStatus === LENDING_STATUS.NOT_LENT &&
      !!this.args.assignedDeskId
    );
  }

  get showOverflowMenu() {
    return !!this.args.groupDeskReservationEnabled || this.canLendDesk;
  }

  get isLentBooked() {
    return this.args.deskLendingEnabled === true && this.lendingStatus === LENDING_STATUS.LENT_BOOKED;
  }

  get lentDeskBookerName() {
    const dateKey = format(this.args.date, 'yyyy-MM-dd');
    return this.args.lendingStatusByDate?.[dateKey]?.reservation?.bookedByName ?? null;
  }

  get canReclaimDesk() {
    return isBefore(startOfDay(new Date()), startOfDay(this.args.date));
  }

  get formattedReclaimDate() {
    return format(this.args.date, 'MMM d, yyyy');
  }

  get showDelegatedBookerName() {
    return this.delegatedBookerName;
  }

  get delegatedBookerName() {
    const { invite } = this.args;
    if (!invite?.employee?.email || !invite?.visitor?.email) {
      return null;
    }

    if (invite.employee.email === invite.visitor.email) {
      return null;
    }

    return invite.employee.name;
  }

  reclaimDeskTask = dropTask(async () => {
    const { assignedDeskId, date } = this.args;

    try {
      await this.scheduleGraphql.reclaimDeskReservation({
        deskId: assignedDeskId,
        date,
      });
      this.args.onLendingStatusChange?.(date, LENDING_STATUS.NOT_LENT);

      // Refetch the day's schedule data so the new invite + reservation appear
      const updated = await this.scheduleGraphql.fetchPartialDayRegistrationByDate({ date });
      if (updated) {
        this.registrations.updateRegistration(date, () => ({
          ...updated,
          date,
        }));
      }

      this.reclaimModalIsOpen = false;
      this.flashMessages.showAndHideFlash('success', 'Desk reclaimed');
    } catch {
      this.flashMessages.showAndHideFlash('error', 'Reclaim desk failed', { details: 'Please try again' });
    }
  });

  lendDeskTask = dropTask(async () => {
    const { assignedDeskId, date } = this.args;

    try {
      await this.deskLending.lendDesk(assignedDeskId, date);
      this.args.onLendingStatusChange?.(date, LENDING_STATUS.LENT_AVAILABLE);
      this.flashMessages.showAndHideFlash('success', 'Desk released');
    } catch {
      this.flashMessages.showAndHideFlash('error', 'Lend desk failed', { details: 'Please try again' });
    }
  });

  signInInviteTask = task({ drop: true }, async () => {
    const { screeningCard, reservation, date } = this.args;

    try {
      const entry = await this.registrations.signInInvitePartialDay(date);
      this.metrics.trackEvent('Web Employee Schedule - Sign In', {
        desk_id: reservation?.desk.id,
        entry_flow_name: entry.flowName,
        entry_id: entry.id,
        entry_location_id: entry.location.id,
        invite_date: screeningCard.expectedArrivalTime,
        invite_id: screeningCard.id,
        reservation_id: reservation?.id,
      });
      this.flashMessages.showAndHideFlash('success', 'Signed in');
    } catch (e) {
      console.error('error', e); // eslint-disable-line no-console
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e.errors?.firstObject));
    }
  });

  signOutEntryTask = task({ drop: true }, async () => {
    const { date, reservation, screeningCard } = this.args;

    try {
      const entry = await this.registrations.signOutEntryPartialDay(date);
      this.metrics.trackEvent('Web Employee Schedule - Sign Out', {
        desk_id: reservation?.desk.id,
        employee_entry_sign_out_at: entry.signedOutAt,
        entry_date: entry.signed,
        entry_flow_name: entry.flowName,
        entry_id: entry.id,
        entry_location_id: entry.location.id,
        invite_id: screeningCard.id,
        reservation_id: reservation?.id,
      });
      this.flashMessages.showAndHideFlash('success', 'Signed out');
    } catch (e) {
      console.error('error', e); // eslint-disable-line no-console
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e.errors?.firstObject));
    }
  });

  unscheduleTask = task({ drop: true }, async () => {
    this.unscheduleError = false;

    try {
      const { invite } = await this.registrations.unschedule(this.args.date, {});
      this.metrics.trackEvent('Web Employee Schedule - Unschedule', {
        desk_id: null,
        invite_date: invite.expectedArrivalTime,
        invite_id: invite.id,
        invite_location_id: invite.location.id,
        reservation_id: null,
      });
      this.unscheduleModalIsOpen = false;
      this.flashMessages.showAndHideFlash('success', 'Reservation released');
    } catch (e) {
      this.unscheduleError = true;
      console.error('error', e); // eslint-disable-line no-console
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e.errors?.firstObject));
    }
  });

  scheduleTask = task({ drop: true }, async () => {
    const { date, bookDeskEnabled } = this.args;
    try {
      const { reservation, invite } = await this.registrations.reserveDeskPartialDay(date);
      this.metrics.trackEvent('Web Employee Schedule - Schedule', {
        desk_id: reservation?.desk.id,
        invite_date: invite.expectedArrivalTime,
        invite_id: invite.id,
        invite_location_id: invite.location.id,
        reservation_id: reservation?.id,
      });
      if (reservation || !bookDeskEnabled) {
        this.flashMessages.showAndHideFlash('success', 'Successfully scheduled');
      } else {
        this.flashMessages.showAndHideFlash('error', 'Desk could not be booked at this time');
      }
    } catch (e) {
      console.error('error', e); // eslint-disable-line no-console
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e.errors?.firstObject));
    }
  });

  @action
  closeUnscheduleModal() {
    this.unscheduleError = false;
    this.unscheduleModalIsOpen = false;
  }

  @action
  closeGroupDeskReservationModal() {
    this.groupDeskReservationModalIsOpen = false;
  }

  @action
  closeReclaimModal() {
    this.reclaimModalIsOpen = false;
  }
}
